import React from 'react';
import { Trans } from 'react-i18next';
import { groupBy } from 'lodash';
import moment from 'moment';
import {
  READY_FOR_INTERNAL_SAQ,
  UNDER_INTERNAL_SAQ,
  READY_FOR_EXTERNAL_SAQ,
  UNDER_EXTERNAL_SAQ,
  LOADING_RESULTS,
  READY_FOR_REVIEW,
  READY_FOR_CAP,
  UNDER_CAP,
  CAP_DEVELOPMENT,
  CAP_COMPLETED,
  CAP_IMPLEMENTATION,
  FINALIZED_CAP,
  FINALIZED,
} from 'src/utils/data/job';
import { notification } from 'src/components/Notification';
import { CompanyType, SurveyType } from 'src/utils/data/types';
import { flatToTree } from 'src/utils/form/section';
import { getValidationVariables } from 'src/utils/form/datasheet';
import { getValidationErrors, resultUnique } from 'src/components/JobDatasheet/utils';
import { generateDefaultDatasheetResults, getSectionResults, applyQuestionConditions as applyQuestionConditionsWithGetResult, } from 'src/components/JobDatasheet/JobDatasheetForm/utils';

const checkValidations = (validationErrors, job, datasheets, jobDatasheetResults, setValidationErrors ) =>{
  const validatedDatasheetIds = Object.keys(validationErrors);
  const needValidationDatasheets = datasheets.filter((datasheet) => {
    return validatedDatasheetIds.indexOf(datasheet.id.toString()) === -1;
  });
  const errorList = Object.values(validationErrors).flat(1);
  const validationErrorList = validationErrors;
  needValidationDatasheets.map( datasheet => {
      const datasheetVersionId = datasheet.currentVersion.id;
      const datasheetId = datasheet.id;   
      const defaultResults = generateDefaultDatasheetResults(datasheet, job);
      let combineResults = defaultResults;
      const resultData = jobDatasheetResults.map( result =>{
        if(result.datasheet_id === datasheetId){
          combineResults = resultUnique((result || { data: [] }).data.concat(defaultResults));
        }
        return result;
      }); 
          
      const sections =  datasheet.currentVersion.datasheetSections || [];
      const validationErrorsChecking = sections.map(section => {
        const sectionResults = getSectionResults(section, combineResults);
        const variables = getValidationVariables(sectionResults);   
        const getResult = question => combineResults.find(
          v => v.datasheetQuestionId === question?.datasheetQuestionId,
        ) || {};         
        const applyQuestionConditions = sectionItem => applyQuestionConditionsWithGetResult(sectionItem, getResult, datasheet);  
        const availableQuestions = applyQuestionConditions(section);
        return availableQuestions.map(question => {
          const questionValidationErrors = getValidationErrors(question, variables, section, combineResults);
          if (questionValidationErrors.length === 0) {
            return null;
          }

          return {
            text: question?.datasheetQuestion?.currentVersion?.text,
            errors: questionValidationErrors,
          };
        }).filter(c => c !== null);
      }).flat(1);
      if(validationErrorsChecking.length > 0) validationErrorList[datasheetId] = validationErrorsChecking;
      errorList.push(...validationErrorsChecking);
      return datasheet;
    });
    setValidationErrors(validationErrorList);
    return errorList;    
}


const checkChecklistValidations = (checklists, jobChecklistResults, setChecklistValidationErrors) =>{
  const errorList = [];
  const validationErrorList = [];

  jobChecklistResults.forEach((checklistResult) => {
    const checklist = checklists.find((item) => item.id === checklistResult.checklist_id);

    const validationErrorsChecking = checklistResult.data
      .flatMap((checklistResultData) => {
        const checklistQuestion = checklist.currentVersion.checklistSections
          .flatMap((checklistSection) => checklistSection.questions)
          .find((question) => question.checklistQuestionId === checklistResultData.checklistQuestionId)
          ?.checklistQuestion?.currentVersion?.text;

        return checklistResultData.nonCompliances
          .filter((nonCompliance) => nonCompliance.nonComplianceId === null)
          .map(() => ({
            text: checklistQuestion,
            errors: 'Non compliance is missing',
          }));
      })
      .filter(Boolean); // Remove `undefined` and falsy values

    if (validationErrorsChecking.length > 0) {
      validationErrorList[checklistResult.checklist_id] = validationErrorsChecking;
    }
    setChecklistValidationErrors(validationErrorList);
    errorList.push(...validationErrorsChecking);
  });
  // Return the final error list
  return errorList;   
}

export const checkValidationErrors = (validationErrors, callback, setIsSubmitting, setHasValidationError, job, datasheets, jobDatasheetResults, setValidationErrors, checklists = [], jobChecklistResults = [], setChecklistValidationErrors = () => {}) => {
  const errors = checkValidations(validationErrors, job, datasheets, jobDatasheetResults, setValidationErrors);

  const checklistErrors = checkChecklistValidations(checklists, jobChecklistResults, setChecklistValidationErrors);
  
  if (errors.length === 0 && checklistErrors.length === 0) {
    callback();
    return false;
  } 
    notification.error(
      <div>
        {errors?.length > 0 && (
          <div>
            <Trans key="JOB_HAS_VALIDATION_ERRORS">JOB_HAS_VALIDATION_ERRORS</Trans>:
            <ul>
              {errors.map((c, i) => (
                <li key={i}>{c.text}</li>
              ))}
            </ul>
          </div>
        )}
        {checklistErrors?.length > 0 && (
          <div>
            <Trans key="JOB_HAS_CHECKLIST_VALIDATION_ERRORS">
              JOB_HAS_CHECKLIST_VALIDATION_ERRORS
            </Trans>:
            <ul>
              {checklistErrors.map((c, i) => (
                <li key={i}>{c.text}</li>
              ))}
            </ul>
          </div>
        )}
      </div>      
    );
    setHasValidationError(true);
    setIsSubmitting(false);
    return true;
  
};

export const generateJobStatusesArray = ({
  allNoteCount,
  closedNoteCount,
  hasCapService,
  isAuditor,
  isFactoryUser,
  isJobReviewer,
  isReviewer,
  job,
  onSetStatus,
  reviewEndAt,
  reviewStartAt,
  setIsSubmitting,
  showConfirmPopupWithProps,
  externalSaqStartAt,
  externalSaqEndAt,
  internalSaqStartAt,
  internalSaqEndAt,
  validationErrors,
  userIsAuthorizedExternalSaqUser,
  hasUnverifiedSaqService,
  isAdmin,
  setHasValidationError,
  datasheets, 
  jobDatasheetResults,
  setValidationErrors
}) => [
  [
    <Trans key="JOB_DOWNLOADED">JOB_DOWNLOADED</Trans>,
    job.downloadedAt,
    null,
    null,
    !job.downloadedAt,
    job.downloadedAt && !job.auditRealStartAt,
  ],
  [
    <Trans key="AUDIT_STARTED">AUDIT_STARTED</Trans>,
    job.auditRealStartAt,
    null,
    null,
    job.downloadedAt && !job.auditRealStartAt,
    job.auditRealStartAt && !job.auditRealFinishAt,
  ],
  [
    <Trans key="AUDIT_FINISHED">AUDIT_FINISHED</Trans>,
    job.auditRealFinishAt,
    null,
    null,
    job.auditRealStartAt && !job.auditRealFinishAt,
    job.auditRealFinishAt && !job.capGeneratedAt,
  ],
  [
    <Trans key="CAP_GENERATED">CAP_GENERATED</Trans>,
    job.capGeneratedAt,
    null,
    null,
    job.auditRealFinishAt && !job.capGeneratedAt,
    job.auditRealFinishAt && !job.uploadedAt,
  ],
  [
    <Trans key="JOB_UPLOADED_TO_EIQ_ASSESS">JOB_UPLOADED_TO_EIQ_ASSESS</Trans>,
    job.uploadedAt,
    null,
    null,
    job.auditRealFinishAt && !job.uploadedAt,
    job.uploadedAt && !reviewStartAt,
    <Trans key="READY_FOR_REVIEW">READY_FOR_REVIEW</Trans>,
  ],
  [
    <Trans key="REVIEW_STARTED">REVIEW_STARTED</Trans>,
    reviewStartAt,
    <Trans key="START">START_REVIEW</Trans>,
    async () => {
      setIsSubmitting(true);
      await onSetStatus({
        jobId: job.id,
        event: 'start_review',
        time: new Date(),
      });
      setIsSubmitting(false);
    },
    (job.uploadedAt || job.status === READY_FOR_REVIEW) && !reviewStartAt && isReviewer && job.status !== LOADING_RESULTS,
    reviewStartAt && !reviewEndAt,
    <Trans key="UNDER_REVIEW">UNDER_REVIEW</Trans>,
  ],
  [
    <Trans key="REVIEW_FINISHED">REVIEW_FINISHED</Trans>,
    reviewEndAt,
    <Trans key="FINISH">FINISH</Trans>,
    () => checkValidationErrors(validationErrors, async () => {
      setIsSubmitting(true);
      await onSetStatus({
        jobId: job.id,
        event: 'finish_review',
        time: new Date(),
      });
      setIsSubmitting(false);
    }, setIsSubmitting, setHasValidationError, job, datasheets, jobDatasheetResults, setValidationErrors),
    reviewStartAt &&
    !reviewEndAt &&
    closedNoteCount === allNoteCount &&
    isJobReviewer &&
    job.status !== LOADING_RESULTS,
    !!reviewEndAt &&
    job.status !== READY_FOR_CAP &&
    job.status !== UNDER_CAP &&
    job.status !== CAP_DEVELOPMENT &&
    job.status !== CAP_IMPLEMENTATION &&
    job.status !== CAP_COMPLETED &&
    job.status !== FINALIZED_CAP,
    !hasCapService ? (
      (()=>(!hasUnverifiedSaqService ?
          <Trans key="REVIEW_FINALIZED">REVIEW_FINALIZED</Trans> :
          <Trans key="FINALIZED">FINALIZED</Trans>))()
    ) : (
      <Trans key="READY_FOR_CAP">READY_FOR_CAP</Trans>
    ),
  ],
  [
    <Trans key="START_EXTERNAL_SAQ">START_EXTERNAL_SAQ</Trans>,
    null,
    <Trans key="START">START</Trans>,
    null,
    false,
    job.status === READY_FOR_EXTERNAL_SAQ && hasUnverifiedSaqService,
    <Trans key="READY_FOR_EXTERNAL_SAQ">READY_FOR_EXTERNAL_SAQ</Trans>,
  ],
  [
    <Trans key="READY_FOR_EXTERNAL_SAQ">EXTERNAL_SAQ_STARTED</Trans>,
    externalSaqStartAt,
    <Trans key="START">START</Trans>,
    async () => {
      setIsSubmitting(true);
      await onSetStatus({
        jobId: job.id,
        event: 'start_external_saq',
        time: new Date(),
      });
      setIsSubmitting(false);
    },
    job.status === READY_FOR_EXTERNAL_SAQ && isFactoryUser && hasUnverifiedSaqService,
    job.status === UNDER_EXTERNAL_SAQ && isFactoryUser && hasUnverifiedSaqService,
    <Trans key="UNDER_EXTERNAL_SAQ">UNDER_EXTERNAL_SAQ</Trans>,
  ],
  [
    <Trans key="UNDER_EXTERNAL_SAQ">EXTERNAL_SAQ_ENDED</Trans>,
    externalSaqStartAt,
    <Trans key="FINISH">FINISH</Trans>,
    () => checkValidationErrors(validationErrors, () => showConfirmPopupWithProps('finish_external_saq_without_internal'), setIsSubmitting, setHasValidationError, job, datasheets, jobDatasheetResults, setValidationErrors),
    job.status === UNDER_EXTERNAL_SAQ && isFactoryUser && hasUnverifiedSaqService,
    job.status === FINALIZED && hasUnverifiedSaqService,
    <Trans key="READY_FOR_INTERNAL_SAQ">READY_FOR_INTERNAL_SAQ</Trans>,
  ],
  [
    <Trans key="START_EXTERNAL_SAQ">START_EXTERNAL_SAQ</Trans>,
    null,
    <Trans key="START">START</Trans>,
    null,
    false,
    job.status === READY_FOR_EXTERNAL_SAQ,
    <Trans key="READY_FOR_EXTERNAL_SAQ">READY_FOR_EXTERNAL_SAQ</Trans>,
  ],
  [
    <Trans key="READY_FOR_EXTERNAL_SAQ">EXTERNAL_SAQ_STARTED</Trans>,
    externalSaqStartAt,
    <Trans key="START">START</Trans>,
    async () => {
      setIsSubmitting(true);
      await onSetStatus({
        jobId: job.id,
        event: 'start_external_saq',
        time: new Date(),
      });
      setIsSubmitting(false);
    },
    job.status === READY_FOR_EXTERNAL_SAQ && (isAdmin || userIsAuthorizedExternalSaqUser) && !hasUnverifiedSaqService,
    job.status === UNDER_EXTERNAL_SAQ && (isAdmin || userIsAuthorizedExternalSaqUser) && !hasUnverifiedSaqService,
    <Trans key="UNDER_EXTERNAL_SAQ">UNDER_EXTERNAL_SAQ</Trans>,
  ],
  [
    <Trans key="UNDER_EXTERNAL_SAQ">EXTERNAL_SAQ_ENDED</Trans>,
    externalSaqEndAt,
    <Trans key="FINISH">FINISH</Trans>,
    () => checkValidationErrors(validationErrors, () => showConfirmPopupWithProps('finish_external_saq'), setIsSubmitting, setHasValidationError, job, datasheets, jobDatasheetResults, setValidationErrors),
    job.status === UNDER_EXTERNAL_SAQ && (isAdmin || userIsAuthorizedExternalSaqUser) && !hasUnverifiedSaqService,
    job.status === READY_FOR_INTERNAL_SAQ && !hasUnverifiedSaqService,
    <Trans key="READY_FOR_INTERNAL_SAQ">READY_FOR_INTERNAL_SAQ</Trans>,
  ],
  [
    <Trans key="START">INTERNAL_SAQ_STARTED</Trans>,
    internalSaqStartAt,
    <Trans key="START">START</Trans>,
    async () => {
      setIsSubmitting(true);
      await onSetStatus({
        jobId: job.id,
        event: 'start_internal_saq',
        time: new Date(),
      });
      setIsSubmitting(false);
    },
    job.status === READY_FOR_INTERNAL_SAQ && isAuditor,
    job.status === UNDER_INTERNAL_SAQ,
    <Trans key="UNDER_INTERNAL_SAQ">UNDER_INTERNAL_SAQ</Trans>,
  ],
  [
    <Trans key="UNDER_INTERNAL_SAQ">INTERNAL_SAQ_ENDED</Trans>,
    internalSaqEndAt,
    <Trans key="FINISH">FINISH</Trans>,
    () =>{ 
      checkValidationErrors(validationErrors, async () => {
      setIsSubmitting(true);
      await onSetStatus({
        jobId: job.id,
        event: 'finish_internal_saq',
        time: new Date(),
      });
      setIsSubmitting(false);
    }, setIsSubmitting, setHasValidationError, job, datasheets, jobDatasheetResults, setValidationErrors)},
    job.status === UNDER_INTERNAL_SAQ && isAuditor && closedNoteCount === allNoteCount,
    job.status === READY_FOR_REVIEW,
    <Trans key="READY_FOR_REVIEW">READY_FOR_REVIEW</Trans>,
  ],
  [
    <Trans key="REVIEW_STARTED">REVIEW_STARTED</Trans>,
    reviewStartAt,
    <Trans key="START">START_REVIEW</Trans>,
    async () => {
      setIsSubmitting(true);
      await onSetStatus({
        jobId: job.id,
        event: 'start_review',
        time: new Date(),
      });
      setIsSubmitting(false);
    },
    !reviewStartAt && isReviewer,
    reviewStartAt && !reviewEndAt,
    <Trans key="UNDER_REVIEW">UNDER_REVIEW</Trans>,
  ],
];

export const getAvailableTabKeys = (checklists, datasheets, surveyDatasheets, additionals) => {
  const checklistsTabs = checklists.map(
    checklist => `checklist-${checklist.id}`,
  );
  const datasheetsTabs = datasheets.map(
    datasheet => `datasheet-${datasheet.id}`,
  );

  const surveyDatasheetsResultsTab = surveyDatasheets.map(
    surveyDatasheet => `surveyDatasheetId-${surveyDatasheet.id}`,
  );

  const additionalTabs = additionals
    .filter(additional => additional.isAvailable)
    .map(additional => additional.key);

  return checklistsTabs
    .concat(datasheetsTabs)
    .concat(surveyDatasheetsResultsTab)
    .concat(additionalTabs);
};

export const getAffiliationOptionsList = (
  job,
  companyTypes = [],
  affiliationTypesToConsider = ['factory', 'vendor'],
  returnCompanyAffiliationMapOnly = false) => {

  const results = [];
  const companies = job.jobCompanies.filter(c => !!c.company);
  const companyAffiliationMap = new Map();

  companies.forEach((companyData) => {
    const { company } = companyData;
    // eslint-disable-next-line consistent-return
    companyTypes.forEach(companyType => {
      if (companyType?.id !== companyData.companyTypeId || !companyType.currentVersion.name || !affiliationTypesToConsider.includes(companyType.currentVersion.name.toLowerCase().trim())) {
        return null;
      }
      const companyIdKey = `${company.currentVersion.companyId}`;
      const companyName = company.currentVersion.name || company.currentVersion.displayName;

      if (companyAffiliationMap.has(companyIdKey)) {
        const affiliationData = companyAffiliationMap.get(companyIdKey);
        companyAffiliationMap.set(companyIdKey, {
          ...affiliationData,
          companyRolesInJob: [
            ...affiliationData.companyRolesInJob,
            companyType.currentVersion.name],
        });
      } else {
        companyAffiliationMap.set(companyIdKey, {
          companyName,
          companyRolesInJob: [companyType.currentVersion.name],
        });
      }
    });
  });

  if (returnCompanyAffiliationMapOnly) {
    return companyAffiliationMap;
  }

  companyAffiliationMap.forEach((value, key) => {
    const companyNameInLabel = value.companyName;
    const companyAffiliationsInLabel = value.companyRolesInJob &&
    Array.isArray(value.companyRolesInJob) &&
    value.companyRolesInJob.length > 0 ?
      ` (${value.companyRolesInJob?.join(', ')})` :
      '';
    results.push({
      value: Number.parseInt(key, 10),
      label: `${companyNameInLabel}${companyAffiliationsInLabel}`,
    });
  });

  return results;
};

export const jobCompanyContactsWithAffiliation = (
  jobCompanyContacts,
  job,
  companyTypes = [],
  affiliationTypesToConsider = ['factory', 'vendor'],
  returnJobCompanyContactAffiliationMapOnly = false) => {
  const results = [];
  const jobCompanyAffiliationMap = getAffiliationOptionsList(
    job,
    companyTypes,
    ['factory', 'vendor'],
    true);

  const jobCompanyContactsAffiliationMap = new Map();

  const groupedJobCompanyContacts = groupBy(jobCompanyContacts, ({ currentVersion }) => (
    `${currentVersion.firstName} ${currentVersion.lastName} (${currentVersion.workEmail})`
  ));

  Object.entries(groupedJobCompanyContacts).forEach(([contactKey, groupedData]) => {
    const jobContactAffiliation = groupedData.map(contactData => {
      return jobCompanyAffiliationMap.has(String(contactData.companyId)) ? {
        contactId: contactData.id,
        companyId: contactData.companyId,
        ...contactData.currentVersion,
        ...jobCompanyAffiliationMap.get(String(contactData.companyId)),
      } : null;
    });
    jobCompanyContactsAffiliationMap.set(contactKey, jobContactAffiliation.filter(affiliation => !!affiliation));
  });

  if (returnJobCompanyContactAffiliationMapOnly) {
    return jobCompanyContactsAffiliationMap;
  }

  jobCompanyContactsAffiliationMap.forEach((value, key) => {
    const contactCompanyAffiliationPart = [].concat(value.map(companyData => companyData.companyRolesInJob).sort()).join(', ');

    results.push({
      label: `${key} (${contactCompanyAffiliationPart})`,
      contactIds: value.map(({ contactId }) => contactId),
    });
  });

  return results;
};

export const getJobFactoryCompanies = (companies, companyTypes) => {
  const factoryTypeId = companyTypes.find(type => type.currentVersion.name === CompanyType.Factory)?.id;
  return companies.filter(company => company.companyTypeId === factoryTypeId);
};

export const getJobProgramCompanies = (companies, companyTypes) => {
  const programTypeId = companyTypes.find(type => type.currentVersion.name === CompanyType.Program)?.id;
  return companies.filter(company => company.companyTypeId === programTypeId);
};

export const sortFindings = (a, b) => {
  // Define the order of severity
  const severityOrder = {
    "Zero Tolerance": 1,
    "Critical": 2,
    "Major": 3,
    "Moderate": 4,
    "Minor": 5,
    "N/A": 6
  };

  // Compare severity first
  if (severityOrder[a.info?.criticality] !== severityOrder[b.info?.criticality]) {
    return severityOrder[a.info.criticality] - severityOrder[b.info.criticality];
  }
  // If severity is the same, compare by checklist question ID in descending order
  return b.checklistQuestionId - a.checklistQuestionId;
};

export const visibilityRuleForReOpenCap = (isNewCapTabVisible, isAdmin, status, capTargetEndAt, findings) =>{
  const capServiceTargetEndAt = moment(capTargetEndAt, 'YYYY-MM-DD');
  const daysAfterCapServiceTargetEndAt = 14;
  if(moment(capServiceTargetEndAt).isBefore(moment().subtract(daysAfterCapServiceTargetEndAt, 'days'))){
    return false;
  }
  const checkAllFindingsCompleted = findings.every(f => ['closed_desktop_verified', 'tentatively_closed_onsite_verification_required'].includes(f.status));

  return isNewCapTabVisible && isAdmin && status === CAP_COMPLETED && !checkAllFindingsCompleted
}
